import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

/**
 * Nephrology calculators — legacy CKD suite (eGFR CKD-EPI 2021, KFRE Tangri
 * 4-variable, ACR tracker). Rendered inline inside the Calculators Hub when
 * the Nephrology tab is active.
 */
export default function NephrologyCalculators() {
  const [profile, setProfile] = useState(null);
  const [scr, setScr] = useState("1.4");
  const [age, setAge] = useState("58");
  const [sex, setSex] = useState("male");
  const [egfrResult, setEgfrResult] = useState(null);

  const [kfreAge, setKfreAge] = useState("58");
  const [kfreSex, setKfreSex] = useState("male");
  const [kfreEgfr, setKfreEgfr] = useState("52");
  const [kfreAcr, setKfreAcr] = useState("88");
  const [kfreResult, setKfreResult] = useState(null);

  const [acrValue, setAcrValue] = useState("");
  const [acrDate, setAcrDate] = useState(new Date().toISOString().slice(0, 10));
  const [acrList, setAcrList] = useState([]);
  const [egfrList, setEgfrList] = useState([]);
  const [logEgfrDate, setLogEgfrDate] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    (async () => {
      const [prof, acr, egfrHist] = await Promise.all([
        api.get("/patient/profile"),
        api.get("/tracker/acr"),
        api.get("/tracker/egfr"),
      ]);
      setProfile(prof.data);
      setAcrList(acr.data);
      setEgfrList(egfrHist.data);
    })();
  }, []);

  const calcEgfr = async () => {
    try {
      const res = await api.post("/calc/egfr", { creatinine: parseFloat(scr), age: parseInt(age), sex });
      setEgfrResult(res.data);
      setKfreEgfr(String(res.data.egfr));
      setKfreAge(age);
      setKfreSex(sex);
    } catch (e) { toast.error("Failed to calculate eGFR"); }
  };

  const logEgfr = async () => {
    if (!egfrResult) { toast.error("Calculate eGFR first"); return; }
    await api.post("/tracker/egfr", { date: logEgfrDate, egfr: egfrResult.egfr, creatinine: parseFloat(scr) });
    const list = await api.get("/tracker/egfr");
    setEgfrList(list.data);
    toast.success("eGFR reading saved");
  };

  const calcKfre = async () => {
    try {
      const res = await api.post("/calc/kfre", {
        age: parseInt(kfreAge), sex: kfreSex,
        egfr: parseFloat(kfreEgfr), acr: parseFloat(kfreAcr),
      });
      setKfreResult(res.data);
    } catch (e) { toast.error("Failed to calculate KFRE"); }
  };

  const logAcr = async () => {
    if (!acrValue) return;
    await api.post("/tracker/acr", { date: acrDate, acr: parseFloat(acrValue) });
    const list = await api.get("/tracker/acr");
    setAcrList(list.data);
    setAcrValue("");
    toast.success("ACR reading saved");
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="calculators-grid">
      <div className="ros-card" data-testid="egfr-calculator">
        <div className="ros-label">eGFR calculator</div>
        <h2 className="font-heading text-2xl mt-1">CKD-EPI 2021</h2>
        <p className="text-xs text-[#A3ACA7] mt-1">Race-free equation. Serum creatinine in mg/dL.</p>
        <div className="grid grid-cols-2 gap-4 mt-6">
          <div>
            <Label className="ros-label" htmlFor="scr">Serum creatinine</Label>
            <Input id="scr" data-testid="egfr-scr-input" value={scr} onChange={(e) => setScr(e.target.value)}
                   type="number" step="0.01" className="mt-2 bg-[#1C201E] border-[#272E2B]" />
          </div>
          <div>
            <Label className="ros-label" htmlFor="age">Age</Label>
            <Input id="age" data-testid="egfr-age-input" value={age} onChange={(e) => setAge(e.target.value)}
                   type="number" className="mt-2 bg-[#1C201E] border-[#272E2B]" />
          </div>
          <div className="col-span-2">
            <Label className="ros-label">Sex</Label>
            <Select value={sex} onValueChange={setSex}>
              <SelectTrigger data-testid="egfr-sex-select" className="mt-2 bg-[#1C201E] border-[#272E2B]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-6 flex gap-3">
          <Button onClick={calcEgfr} data-testid="egfr-calc-btn"
                  className="rounded-full bg-[#4ADE80] text-black hover:bg-[#4ADE80]/90 font-semibold">
            Calculate
          </Button>
          {egfrResult && (
            <div className="flex-1 flex items-center gap-3">
              <Input data-testid="egfr-log-date" value={logEgfrDate} onChange={(e) => setLogEgfrDate(e.target.value)}
                     type="date" className="bg-[#1C201E] border-[#272E2B] max-w-[180px]" />
              <Button onClick={logEgfr} variant="outline" data-testid="egfr-log-btn"
                      className="rounded-full border-[#272E2B] hover:bg-[#1C201E]">
                Log to history
              </Button>
            </div>
          )}
        </div>
        {egfrResult && (
          <div className="mt-6 ros-card-raised">
            <div className="flex items-end gap-4">
              <div className="ros-metric text-5xl" data-testid="egfr-result-value">{egfrResult.egfr.toFixed(1)}</div>
              <div>
                <div className="text-xs text-[#A3ACA7]">mL/min/1.73m²</div>
                <div className="text-sm mt-1">Stage <span className="text-white font-semibold">{egfrResult.stage}</span> · {egfrResult.label}</div>
              </div>
            </div>
            <p className="text-xs text-[#A3ACA7] mt-3">{egfrResult.description}</p>
          </div>
        )}
        {egfrList.length > 0 && (
          <div className="mt-6 h-[140px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={egfrList} margin={{ top: 10, right: 10, bottom: 0, left: -30 }}>
                <CartesianGrid stroke="#1C201E" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" stroke="#A3ACA7" fontSize={10} tickLine={false} axisLine={false}
                       tickFormatter={(v) => (v || "").slice(5)} />
                <YAxis stroke="#A3ACA7" fontSize={10} tickLine={false} axisLine={false} width={30} />
                <Tooltip contentStyle={{ background: "#121514", border: "1px solid #272E2B", borderRadius: 12 }} />
                <Line type="monotone" dataKey="egfr" stroke="#4ADE80" strokeWidth={3} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="ros-card" data-testid="kfre-calculator">
        <div className="ros-label">Kidney failure risk equation</div>
        <h2 className="font-heading text-2xl mt-1">KFRE — Tangri 4-variable</h2>
        <p className="text-xs text-[#A3ACA7] mt-1">Predicts 2- and 5-year risk of kidney failure requiring dialysis or transplant.</p>
        <div className="grid grid-cols-2 gap-4 mt-6">
          <div>
            <Label className="ros-label">Age</Label>
            <Input value={kfreAge} onChange={(e) => setKfreAge(e.target.value)} type="number"
                   data-testid="kfre-age-input" className="mt-2 bg-[#1C201E] border-[#272E2B]" />
          </div>
          <div>
            <Label className="ros-label">Sex</Label>
            <Select value={kfreSex} onValueChange={setKfreSex}>
              <SelectTrigger className="mt-2 bg-[#1C201E] border-[#272E2B]" data-testid="kfre-sex-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="ros-label">eGFR</Label>
            <Input value={kfreEgfr} onChange={(e) => setKfreEgfr(e.target.value)} type="number" step="0.1"
                   data-testid="kfre-egfr-input" className="mt-2 bg-[#1C201E] border-[#272E2B]" />
          </div>
          <div>
            <Label className="ros-label">ACR (mg/g)</Label>
            <Input value={kfreAcr} onChange={(e) => setKfreAcr(e.target.value)} type="number"
                   data-testid="kfre-acr-input" className="mt-2 bg-[#1C201E] border-[#272E2B]" />
          </div>
        </div>
        <Button onClick={calcKfre} data-testid="kfre-calc-btn"
                className="mt-6 rounded-full bg-[#4ADE80] text-black hover:bg-[#4ADE80]/90 font-semibold">
          Calculate risk
        </Button>
        {kfreResult && (
          <div className="mt-6 grid grid-cols-2 gap-4">
            <div className="ros-card-raised" data-testid="kfre-2yr">
              <div className="ros-label">2-year risk</div>
              <div className="ros-metric text-4xl mt-2">{kfreResult.risk_2yr.toFixed(2)}%</div>
            </div>
            <div className="ros-card-raised" data-testid="kfre-5yr">
              <div className="ros-label">5-year risk</div>
              <div className="ros-metric text-4xl mt-2">{kfreResult.risk_5yr.toFixed(2)}%</div>
            </div>
          </div>
        )}
      </div>

      <div className="ros-card lg:col-span-2" data-testid="acr-tracker">
        <div className="flex items-center justify-between">
          <div>
            <div className="ros-label">Urine ACR tracker</div>
            <h2 className="font-heading text-2xl mt-1">Albumin-to-creatinine ratio</h2>
            <p className="text-xs text-[#A3ACA7] mt-1">Track proteinuria — often earlier signal than eGFR change.</p>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <Label className="ros-label">Date</Label>
              <Input value={acrDate} onChange={(e) => setAcrDate(e.target.value)} type="date"
                     data-testid="acr-date-input" className="mt-2 bg-[#1C201E] border-[#272E2B]" />
            </div>
            <div>
              <Label className="ros-label">ACR (mg/g)</Label>
              <Input value={acrValue} onChange={(e) => setAcrValue(e.target.value)} type="number"
                     data-testid="acr-value-input" className="mt-2 bg-[#1C201E] border-[#272E2B]" />
            </div>
            <Button onClick={logAcr} data-testid="acr-log-btn"
                    className="rounded-full bg-[#4ADE80] text-black hover:bg-[#4ADE80]/90 font-semibold">
              Log
            </Button>
          </div>
        </div>
        {acrList.length > 0 && (
          <div className="mt-6 h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={acrList} margin={{ top: 10, right: 10, bottom: 0, left: -20 }}>
                <CartesianGrid stroke="#1C201E" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" stroke="#A3ACA7" fontSize={11} tickLine={false} axisLine={false}
                       tickFormatter={(v) => (v || "").slice(5)} />
                <YAxis stroke="#A3ACA7" fontSize={11} tickLine={false} axisLine={false} width={40} />
                <Tooltip contentStyle={{ background: "#121514", border: "1px solid #272E2B", borderRadius: 12 }} />
                <Line type="monotone" dataKey="acr" stroke="#FBBF24" strokeWidth={3} dot={{ r: 3, fill: "#FBBF24" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
